// Shared table renderer for the "Npt strangle @ 3:50 PM, hold to close"
// backtests (backend/cmd/spx20ptbacktest -distance=N) - one-off historical
// runs over a fixed 30-day window with SPX opens supplied by the user, not a
// live/re-runnable strategy, so results are hardcoded per-distance in
// SPX20PointStrangleTable.js / SPX5PointStrangleTable.js rather than
// fetched. This file only renders; those two own their ROWS data.
//
// Row shape: [date, spxSpot, type, strike, status, ...statusSpecificFields, budgetAfter]
//   status 'filled':         [..., buy, closed, high, low, pnlDollar, pnlPct, budgetAfter]
//   status 'skipped_budget': [..., buy, budgetAfter]
//   status 'skipped_data':   [..., null, budgetAfter]
// budgetAfter is always the last element regardless of shape.
export default function SPXStrangleBacktestTable({ title, subtitle, rows, startBudget = 500 }) {
  const filled = rows.filter(r => r[4] === 'filled');
  const totalPnl = filled.reduce((s, r) => s + r[9], 0);
  const endBudget = rows.length ? rows[rows.length - 1][rows[rows.length - 1].length - 1] : startBudget;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {title}
        </span>
        <span className="text-xs font-mono text-gray-400">{filled.length}/{rows.length} legs filled</span>
        <span className="text-xs font-mono" style={{ color: endBudget >= startBudget ? '#22c55e' : '#ef4444' }}>
          Budget: ${startBudget.toFixed(2)} → ${endBudget.toFixed(2)}
        </span>
        <span className="text-xs font-mono" style={{ color: totalPnl >= 0 ? '#22c55e' : '#ef4444' }}>
          Total P/L: {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
        </span>
      </div>
      <div className="text-xs text-gray-500 mb-3">{subtitle}</div>

      <div className="overflow-x-auto" style={{ maxHeight: 480, overflowY: 'auto' }}>
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead className="sticky top-0" style={{ backgroundColor: '#111827' }}>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Date', 'SPX Spot', 'Type', 'Strike', 'Buy Price', 'Closed Price', 'Highest (from entry)', 'Lowest (from entry)', 'Gain/Loss', 'Gain/Loss %', 'Budget After'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium whitespace-nowrap ${h === 'Date' || h === 'Type' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const [date, spot, type, strike, status] = r;
              const isCall = type === 'CALL';
              const typeColor = isCall ? '#22c55e' : '#ef4444';
              const budgetAfter = r[r.length - 1];

              if (status !== 'filled') {
                const buy = r[5];
                const reason = status === 'skipped_data'
                  ? 'no price data at 3:50 PM'
                  : `budget too low ($${buy != null ? (buy * 100).toFixed(2) : '?'} needed)`;
                return (
                  <tr key={i} className="border-b" style={{ borderColor: '#1f2937', opacity: 0.5 }}>
                    <td className="py-1.5 pr-3 text-gray-400">{date}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{spot.toFixed(2)}</td>
                    <td className="py-1.5 pr-3">
                      <span className="font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: typeColor + '15', color: typeColor }}>{type}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{strike}</td>
                    <td colSpan={5} className="py-1.5 pr-3 text-gray-600 italic">Skipped — {reason}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-500">${budgetAfter.toFixed(2)}</td>
                  </tr>
                );
              }

              const [, , , , , buy, closed, high, low, pnlDollar, pnlPct] = r;
              const color = pnlDollar >= 0 ? '#22c55e' : '#ef4444';
              return (
                <tr key={i} className="border-b hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                  <td className="py-1.5 pr-3 text-gray-300">{date}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{spot.toFixed(2)}</td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: typeColor + '22', color: typeColor }}>{type}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#60a5fa' }}>{strike}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${buy.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${closed.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${high.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${low.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
                    {pnlDollar >= 0 ? '+' : ''}${pnlDollar.toFixed(2)}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
                    {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${budgetAfter.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
